import clsx from 'clsx'
import React, { useCallback, useMemo } from 'react'

interface Option {
  value: string
  label: string
}

interface TagSelectProps {
  options: Option[]
  value?: string[]
  onChange: (value: string[]) => void
  name: string
  multiple?: boolean
  invalid?: boolean
  className?: string
}

export const TagSelect: React.FC<TagSelectProps> = ({
  options,
  value: inputValue,
  onChange,
  name,
  invalid = false,
  multiple = false,
  className = ''
}) => {
  const value = useMemo(() => inputValue ?? [], [inputValue])
  const valueSet = useMemo(() => new Set(value), [value])

  const handleClick = useCallback(
    (optionValue: string) => {
      if (multiple) {
        const alreadySelected = valueSet.has(optionValue)

        const newValue = alreadySelected
          ? value.filter(v => v !== optionValue)
          : [...value, optionValue]

        onChange(newValue)
      } else {
        const alreadySelected = valueSet.has(optionValue)
        const newValue = alreadySelected ? [] : [optionValue]

        onChange(newValue)
      }
    },
    [value, valueSet, multiple, onChange]
  )

  return (
    <div
      className={clsx(
        'flex flex-wrap gap-2',
        invalid && 'border border-red-500 rounded-md p-2',
        className
      )}
    >
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleClick(option.value)}
          className={clsx(
            'px-3 py-1.5 rounded-small text-regular font-medium transition-colors',
            valueSet.has(option.value)
              ? ['bg-primary text-white border border-primary']
              : [
                  'border border-secondary bg-transparent text-gray-700 hover:bg-gray-50'
                ]
          )}
        >
          {option.label}
        </button>
      ))}
      <input type="hidden" name={name} value={value.join(',')} />
    </div>
  )
}
